import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArticleIcon from '@mui/icons-material/Article';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TutorStructureNode } from '../../../services/tutorStructureService';

interface TreeNodeProps {
  node: TutorStructureNode;
  depth: number;
  onSelect: (node: TutorStructureNode) => void;
  onDelete: (node: TutorStructureNode) => void;
  onContentEdit?: (nodeId: number) => void;
}

export default function TreeNode({ node, depth, onSelect, onDelete, onContentEdit }: TreeNodeProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: node.id,
    data: { type: 'TREE_NODE', node },
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const nodeIcon =
    node.nodeType === 'WRAPPER' ? (
      <FolderOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />
    ) : node.isLibraryLinked ? (
      <LinkIcon sx={{ color: 'primary.main', fontSize: 20 }} />
    ) : (
      <Box sx={{ width: 20 }} />
    );

  return (
    <Box ref={setNodeRef} style={style}>
      <ListItem
        dense
        onClick={() => onSelect(node)}
        sx={{
          pl: 1 + depth * 2,
          cursor: 'pointer',
          borderRadius: 1,
          border: isOver ? '1px dashed' : '1px solid transparent',
          borderColor: isOver ? 'primary.main' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <IconButton size="small" sx={{ cursor: 'grab', mr: 0.5, p: 0.25 }} {...listeners} {...attributes}>
          <DragIndicatorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </IconButton>
        <ListItemIcon sx={{ minWidth: 28 }}>{nodeIcon}</ListItemIcon>
        <ListItemText
          primary={node.label}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
        />
        {node.description && (
          <Tooltip title={node.description} placement="right">
            <NotesIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
          </Tooltip>
        )}
        {node.isLibraryLinked && (
          <Chip label="Library" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10, mr: 0.5 }} />
        )}
        {onContentEdit && node.nodeType !== 'WRAPPER' && (
          <Tooltip title="Edit content">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onContentEdit(node.id);
              }}
              sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <ArticleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(node);
          }}
          sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </ListItem>

      {node.children && node.children.length > 0 && (
        <Box>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onSelect={onSelect}
              onDelete={onDelete}
              onContentEdit={onContentEdit}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
